import { ChatActionsMap } from "components/KBo/Models/ChatAction";
import BaseService from "./BaseService";
import {
    ServerAction,
    ChatServerSuggestion,
} from "components/KBo/Models/ChatSuggestion";
import { MessageAuthor } from "components/KBo/Models/MessageAuthor";
import {
    ApplicationFilters,
    SavedFilter,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { OrganizationKBoStatus } from "components/KBo/Models/OrganizationKBoStatus";
import { FeedbackType } from "components/Signals/BaseObjects/ChartCard/ChartFeatureStores/AISummaryStore";
import type {
    KboAvailableModel,
    KboSkillDefinition,
    KboSettings,
    ToolDefinition,
} from "components/KBo/Models/KboSettings";
import type { AgentArtifact } from "components/KBo/Models/AgentArtifact";
import { ApplicationFilterSubscription } from "./ApplicationFiltersService";

export type NewChatPayload = {
    id: string;
};

export const KboChatIdResponseHeader = "Acx-Agent-ChatId";

export type EndStreamMessageIcon = "Default" | "Hourglass";

export interface EndStreamMessage {
    message: string;
    icon?: EndStreamMessageIcon;
}

export interface StreamingTokenResult {
    text: string;
    actions?: ChatActionsMap;
    suggestions?: ChatServerSuggestion[];
    thinkingMessage?: string;
    thinkingMessages?: string[];
    endMessage?: EndStreamMessage;
    functionCalls?: Record<string, unknown>[];
    reasoningSummaries?: import("components/KBo/Models/ChatMessageGroup").ReasoningSummary[];
    artifacts?: AgentArtifact[];
}

export interface SimilarClipsRequest {
    soundClipId: string;
    additionalClips: number;
}

export interface AgentFilterContext {
    contextType: string;
    filters: string | (ApplicationFilters & { conversationIds?: string[] });
    chartId?: string;
    chartTitle?: string;
    endpointId?: string;
    contextSummary?: string;
    dataPointQueryContext?: DataPointQueryContext;
    insightType?: string;
    insightTitle?: string;
}

export interface DataPointQueryContext {
    moduleId?: string;
    questionId?: string;
}

interface CoachingOpportunity {
    addresses: string[];
    feedback: string;
    actionItem: string;
}

interface CoachingNotesResponseData {
    strengths: string[];
    opportunities: CoachingOpportunity[];
    summary: string;
}

interface GenerateCoachingNotesSuccessResponse {
    successful: true;
    response: CoachingNotesResponseData;
}

interface GenerateCoachingNotesFailureResponse {
    successful: false;
    message: string;
}

type GenerateCoachingNotesResponse =
    | GenerateCoachingNotesSuccessResponse
    | GenerateCoachingNotesFailureResponse;

export class AcxAgentService extends BaseService {
    protected urlBase = "api/aiagent";

    public async getOrganizationKboStatus(): Promise<OrganizationKBoStatus> {
        const apiPath = "kbo-status";

        const response = await this.get<OrganizationKBoStatus>(
            new URLSearchParams(),
            apiPath,
        );

        return response;
    }

    public async persistMessage(
        chatId: string | undefined,
        message: {
            author: MessageAuthor;
            content: string;
            additionalInformation?: Record<string, unknown>;
        },
        newChat?: {
            name?: string;
            kboSessionSettings?: KboSettings | null;
        },
    ): Promise<NewChatPayload> {
        const urlParams = new URLSearchParams();
        const apiPath = chatId ? `chat/${chatId}/add` : "chat/add";

        return this.post(
            JSON.stringify({
                ...message,
                chatName: newChat?.name,
                kboSessionSettings:
                    newChat?.kboSessionSettings ?? undefined,
            }),
            urlParams,
            apiPath,
        );
    }

    private async *createStreamedResponseAsyncGenerator(response: Response) {
        if (!response.ok) {
            const errorText = await response.text().catch(() => "");
            throw new Error(
                `KBo stream failed with ${response.status}: ${errorText}`,
            );
        }

        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error("No response reader.");
        }

        const decoder = new TextDecoder();
        let buffer = "";

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const parsed = this.extractJsonObjectsFromBuffer(buffer);
                buffer = parsed.remaining;

                for (const token of parsed.tokens) {
                    yield token;
                }
            }

            buffer += decoder.decode();
            const parsed = this.extractJsonObjectsFromBuffer(buffer);
            for (const token of parsed.tokens) {
                yield token;
            }

            if (parsed.remaining.trim()) {
                console.error(
                    "Failed to parse trailing streaming JSON chunk:",
                    parsed.remaining,
                );
            }
        } finally {
            reader.releaseLock();
        }
    }

    /**
     * Extracts complete top-level JSON objects from the stream buffer.
     * The backend streams adjacent JSON objects without delimiters, and large
     * action payloads can be split across reads. This parser keeps incomplete
     * objects buffered and ignores braces inside JSON strings.
     */
    private extractJsonObjectsFromBuffer(buffer: string): {
        tokens: StreamingTokenResult[];
        remaining: string;
    } {
        const tokens: StreamingTokenResult[] = [];
        let depth = 0;
        let start = -1;
        let inString = false;
        let escaped = false;
        let consumedThrough = 0;

        for (let i = 0; i < buffer.length; i++) {
            const char = buffer[i];

            if (start === -1) {
                if (char === "{") {
                    start = i;
                    depth = 1;
                    inString = false;
                    escaped = false;
                } else if (!/\s/.test(char)) {
                    consumedThrough = i + 1;
                }

                continue;
            }

            if (escaped) {
                escaped = false;
                continue;
            }

            if (inString && char === "\\") {
                escaped = true;
                continue;
            }

            if (char === '"') {
                inString = !inString;
                continue;
            }

            if (inString) {
                continue;
            }

            if (char === "{") {
                depth++;
            } else if (char === "}") {
                depth--;
                if (depth === 0 && start !== -1) {
                    const raw = buffer.slice(start, i + 1);

                    try {
                        const parsed: StreamingTokenResult = JSON.parse(raw);
                        tokens.push(parsed);
                    } catch (error) {
                        console.error(
                            "Failed to parse JSON chunk:",
                            error,
                            "raw:",
                            raw,
                        );
                    }

                    consumedThrough = i + 1;
                    start = -1;
                    inString = false;
                    escaped = false;
                }
            }
        }

        return {
            tokens,
            remaining:
                start === -1
                    ? buffer.slice(consumedThrough).trimStart()
                    : buffer.slice(start),
        };
    }

    private notifyChatStarted(
        response: Response,
        onChatStarted?: (chatId: string) => void,
    ) {
        const chatId = response.headers.get(KboChatIdResponseHeader);
        if (chatId) {
            onChatStarted?.(chatId);
        }
    }

    public async *streamingSubmitMessage(
        request: {
            chatId?: string;
            body: string;
            availableActions:
                | {
                      suggestionText: string;
                      llmInput?: string;
                      serverAction?: ServerAction;
                  }[]
                | undefined;
            serverAction?: ServerAction;
            similarClips?: SimilarClipsRequest;
            kboSessionSettings?: KboSettings | null;
        },
        additionalInformation?: Record<string, unknown>,
        filterContext?: AgentFilterContext,
        abortSignal?: AbortSignal,
        onChatStarted?: (chatId: string) => void,
    ): AsyncIterable<StreamingTokenResult> {
        const urlParams = new URLSearchParams();
        if (request.chatId) {
            urlParams.set("chatId", request.chatId);
        }
        const apiPath = "chat/ask";

        const response: Response = await this.post(
            JSON.stringify({
                input: request.body,
                additionalInformation,
                availableChoices: request.availableActions,
                serverAction: request.serverAction,
                similarClips: request.similarClips,
                removeStreamDelay: (window as any).__kbo_removeStreamDelay,
                simulateBuffering: (window as any).__kbo_simulateBuffering,
                appliedFilterContext: filterContext,
                kboSessionSettings: request.kboSessionSettings ?? undefined,
            }),
            urlParams,
            apiPath,
            undefined,
            undefined,
            abortSignal,
            true,
        );

        this.notifyChatStarted(response, onChatStarted);

        for await (const result of this.createStreamedResponseAsyncGenerator(
            response,
        )) {
            yield result;
        }
    }

    public async updateChatSessionSettings(
        chatId: string,
        settings: KboSettings | null,
    ): Promise<void> {
        await this.put(
            new URLSearchParams(),
            `chat/${chatId}/settings/session`,
            JSON.stringify({ settings }),
        );
    }

    public async generateCoachingNotes(
        agentId: string,
        audioMetadataId: string,
        abortSignal?: AbortSignal,
    ) {
        const apiPath = `generate-coaching-notes`;
        const result = await this.post<GenerateCoachingNotesResponse>(
            JSON.stringify({ agentId, audioMetadataId }),
            new URLSearchParams(),
            apiPath,
            undefined,
            undefined,
            abortSignal,
        );
        return result;
    }

    public async *analyzeProactiveInsight(
        chatId: string | undefined,
        insightType: string,
        filterContext: AgentFilterContext,
        analysisSubject: string,
        abortSignal?: AbortSignal,
        onChatStarted?: (chatId: string) => void,
    ): AsyncIterable<StreamingTokenResult> {
        const urlParams = new URLSearchParams();
        if (chatId) {
            urlParams.set("chatId", chatId);
        }
        const apiPath = "chat/analyze-proactive-insight";
        const response = await this.post<Response>(
            JSON.stringify({
                insightType,
                appliedFilterContext: filterContext,
                removeStreamDelay: (window as any).__kbo_removeStreamDelay,
                simulateBuffering: (window as any).__kbo_simulateBuffering,
                additionalInformation: {
                    analysisSubject,
                    frontendPersisted: true,
                },
            }),
            urlParams,
            apiPath,
            undefined,
            undefined,
            abortSignal,
            true,
        );

        this.notifyChatStarted(response, onChatStarted);

        for await (const result of this.createStreamedResponseAsyncGenerator(
            response,
        )) {
            yield result;
        }
    }

    public async *analyzeDataPoint(
        chatId?: string,
        filterContext?: AgentFilterContext,
        abortSignal?: AbortSignal,
        onChatStarted?: (chatId: string) => void,
    ): AsyncIterable<StreamingTokenResult> {
        const urlParams = new URLSearchParams();
        if (chatId) {
            urlParams.set("chatId", chatId);
        }
        const apiPath = "chat/analyze-data-point";
        const response = await this.post<Response>(
            JSON.stringify({
                appliedFilterContext: filterContext,
                removeStreamDelay: (window as any).__kbo_removeStreamDelay,
                simulateBuffering: (window as any).__kbo_simulateBuffering,
                AdditionalInformation: {
                    FrontendPersisted: true,
                },
            }),
            urlParams,
            apiPath,
            undefined,
            undefined,
            abortSignal,
            true,
        );

        this.notifyChatStarted(response, onChatStarted);

        for await (const result of this.createStreamedResponseAsyncGenerator(
            response,
        )) {
            yield result;
        }
    }

    public async downloadChats(startDate?: string, endDate?: string) {
        const apiPath = `chat/download`;

        const response = await this.post<Response>(
            JSON.stringify({ startDate, endDate }),
            new URLSearchParams(),
            apiPath,
            undefined,
            undefined,
            undefined,
            true,
        );

        return response;
    }

    /**
     * Sets the feedback on the most recent agent sent message
     * @param chatId
     */
    public async provideFeedback(
        chatId: string,
        feedbackType: FeedbackType | null,
        feedback: string | null,
    ): Promise<void> {
        const apiPath = `chat/${chatId}/feedback`;

        return this.post<void>(
            JSON.stringify({ feedbackType, feedback }),
            new URLSearchParams(),
            apiPath,
        );
    }

    /**
     * Gets all chat sessions within a date range
     * @param startDate - Start date for filtering chats
     * @param endDate - End date for filtering chats
     * @param organizationId - Optional organization ID filter
     */
    public async getAllChats(
        startDate?: string,
        endDate?: string,
        organizationId?: string,
        abortSignal?: AbortSignal,
    ): Promise<ChatSession[]> {
        const apiPath = `chat/getall`;

        return this.post<ChatSession[]>(
            JSON.stringify({ startDate, endDate, organizationId }),
            new URLSearchParams(),
            apiPath,
            undefined,
            undefined,
            abortSignal,
        );
    }

    /**
     * Gets chat sessions with pagination support
     * @param startDate - Start date for filtering chats
     * @param endDate - End date for filtering chats
     * @param organizationId - Optional organization ID filter
     * @param searchString - Optional search string filter
     * @param skip - Number of records to skip
     * @param take - Number of records to take
     */
    public async getChatsPaginated(
        startDate?: string,
        endDate?: string,
        organizationId?: string,
        searchString?: string,
        skip: number = 0,
        take: number = 20,
        abortSignal?: AbortSignal,
    ): Promise<{ chats: ChatSession[]; totalCount: number }> {
        const apiPath = `chat/getpaginated`;

        return this.post<{ chats: ChatSession[]; totalCount: number }>(
            JSON.stringify({
                startDate,
                endDate,
                organizationId,
                searchString,
                skip,
                take,
            }),
            new URLSearchParams(),
            apiPath,
            undefined,
            undefined,
            abortSignal,
        );
    }

    public async getMyChats({
        page,
        pageSize,
        searchString,
        abortSignal,
    }: {
        page: number;
        pageSize: number;
        searchString?: string;
        abortSignal?: AbortSignal;
    }): Promise<{
        chats: Omit<ChatSession, "messages">[];
        totalCount: number;
    }> {
        const apiPath = `chat/me`;

        return this.post<{
            chats: Omit<ChatSession, "messages">[];
            totalCount: number;
        }>(
            JSON.stringify({ page, pageSize, searchString }),
            new URLSearchParams(),
            apiPath,
            undefined,
            undefined,
            abortSignal,
        );
    }

    public async summarizeModelResults(
        filters: ApplicationFilters,
        llmClassifierId: string,
        result?: string | null,
        abortSignal?: AbortSignal,
    ): Promise<{ summary: string; sql: string }> {
        const apiPath = "model-results-summary";

        return this.post(
            JSON.stringify({ filters, llmClassifierId, result }),
            new URLSearchParams(),
            apiPath,
            undefined,
            undefined,
            abortSignal,
        );
    }

    public async getChatById({
        chatId,
        abortSignal,
    }: {
        chatId: string;
        abortSignal?: AbortSignal;
    }): Promise<ChatSession | null> {
        const apiPath = `chat/${chatId}`;

        return this.get<ChatSession | null>(
            new URLSearchParams(),
            apiPath,
            abortSignal,
        );
    }

    public async deleteChatById(chatId: string): Promise<Response> {
        const apiPath = `chat/${chatId}`;
        return this.delete(
            new URLSearchParams(),
            apiPath,
            JSON.stringify({
                id: chatId,
            }),
        );
    }

    public async generateChatName({
        input,
        chatId,
    }: {
        input: string;
        chatId: string;
    }): Promise<{ name: string } | null> {
        const apiPath = `chat/${chatId}/name`;

        const response = await this.post<{ name: string } | null>(
            JSON.stringify({ input }),
            new URLSearchParams(),
            apiPath,
        );

        return response;
    }

    public async setChatName(chatId: string, name?: string): Promise<void> {
        const apiPath = `chat/${chatId}/name`;

        await this.patch<void>(
            JSON.stringify({ name }),
            new URLSearchParams(),
            apiPath,
        );
    }

    public async requestAccessToKbo() {
        const apiPath = "request-access";

        return this.post(
            JSON.stringify({}),
            new URLSearchParams(),
            apiPath,
            undefined,
            undefined,
        );
    }

    async clearAgentAnalysisState(chatId: string) {
        const apiPath = `chat/${chatId}/state/analysis/clear`;
        return this.post(
            JSON.stringify({}),
            new URLSearchParams(),
            apiPath,
            undefined,
            undefined,
        );
    }

    public async getOrgKboSettings(): Promise<KboSettings | null> {
        return this.get<KboSettings | null>(
            new URLSearchParams(),
            "settings/org",
        );
    }

    public async updateOrgKboSettings(settings: KboSettings): Promise<void> {
        return this.put(
            new URLSearchParams(),
            "settings/org",
            JSON.stringify(settings),
        );
    }

    public async getUserKboSettings(
        userId?: string,
    ): Promise<KboSettings | null> {
        const path = userId ? `settings/user/${userId}` : "settings/user";
        return this.get<KboSettings | null>(new URLSearchParams(), path);
    }

    public async updateUserKboSettings(
        settings: KboSettings,
        userId?: string,
    ): Promise<void> {
        const path = userId ? `settings/user/${userId}` : "settings/user";
        return this.put(new URLSearchParams(), path, JSON.stringify(settings));
    }

    public async getAvailableSkills(): Promise<KboSkillDefinition[]> {
        type RawKboSkillDefinition = {
            name?: string;
            description?: string;
            toolNames?: string[] | null;
            toolCount?: number;
            Name?: string;
            Description?: string;
            ToolNames?: string[] | null;
            ToolCount?: number;
        };

        const skills = await this.get<RawKboSkillDefinition[]>(
            new URLSearchParams(),
            "skills",
        );

        return (skills ?? [])
            .map((skill) => {
                const toolNames = skill.toolNames ?? skill.ToolNames ?? [];
                return {
                    name: skill.name ?? skill.Name ?? "",
                    description: skill.description ?? skill.Description ?? "",
                    toolNames,
                    toolCount:
                        skill.toolCount ?? skill.ToolCount ?? toolNames.length,
                };
            })
            .filter((skill) => skill.name.length > 0);
    }

    public async getAvailableTools(): Promise<ToolDefinition[]> {
        type RawToolDefinition = {
            functionName?: string;
            description?: string;
            pluginName?: string;
            namespace?: string | null;
            configurableProperties?: string[] | null;
            isInfrastructure?: boolean;
            defaultLoadingMode?: "deferred" | "eager";
            FunctionName?: string;
            Description?: string;
            PluginName?: string;
            Namespace?: string | null;
            ConfigurableProperties?: string[] | null;
            IsInfrastructure?: boolean;
            DefaultLoadingMode?: "deferred" | "eager";
        };

        const tools = await this.get<RawToolDefinition[]>(
            new URLSearchParams(),
            "tools",
        );

        return (tools ?? [])
            .map((tool) => ({
                functionName: tool.functionName ?? tool.FunctionName ?? "",
                description: tool.description ?? tool.Description ?? "",
                pluginName: tool.pluginName ?? tool.PluginName ?? "",
                namespace: tool.namespace ?? tool.Namespace,
                configurableProperties:
                    tool.configurableProperties ??
                    tool.ConfigurableProperties ??
                    [],
                isInfrastructure:
                    tool.isInfrastructure ?? tool.IsInfrastructure ?? false,
                defaultLoadingMode:
                    tool.defaultLoadingMode ??
                    tool.DefaultLoadingMode ??
                    "deferred",
            }))
            .filter((tool) => tool.functionName.length > 0);
    }

    public async getResolvedSettings(): Promise<KboSettings> {
        return this.get<KboSettings>(
            new URLSearchParams(),
            "settings/resolved",
        );
    }

    public async getTenantDefaults(): Promise<KboSettings> {
        return this.get<KboSettings>(
            new URLSearchParams(),
            "settings/tenant-defaults",
        );
    }

    public async getGlobalDefaults(): Promise<KboSettings> {
        return this.get<KboSettings>(
            new URLSearchParams(),
            "settings/defaults",
        );
    }

    public async getAvailableModels(): Promise<KboAvailableModel[]> {
        type RawKboAvailableModel = {
            name?: string;
            isThinkingModel?: boolean;
            thinkingEffortLevels?: string[] | null;
            Name?: string;
            IsThinkingModel?: boolean;
            ThinkingEffortLevels?: string[] | null;
        };

        const models = await this.get<(RawKboAvailableModel | string)[]>(
            new URLSearchParams(),
            "models",
        );

        return models.map((model) => {
            if (typeof model === "string") {
                return {
                    name: model,
                    isThinkingModel: false,
                    thinkingEffortLevels: [],
                };
            }

            return {
                name: model.name ?? model.Name ?? "",
                isThinkingModel:
                    model.isThinkingModel ?? model.IsThinkingModel ?? false,
                thinkingEffortLevels:
                    model.thinkingEffortLevels ??
                    model.ThinkingEffortLevels ??
                    [],
            };
        });
    }

    public async getAvailableEmbeddingModels(): Promise<string[]> {
        return this.get<string[]>(
            new URLSearchParams(),
            "models/embedding",
        );
    }

    public async getTemplateTags(): Promise<Record<string, string[]>> {
        return this.get<Record<string, string[]>>(
            new URLSearchParams(),
            "settings/template-tags",
        );
    }

    public async getSavedPresets(): Promise<Record<string, KboSettings>> {
        return this.get<Record<string, KboSettings>>(
            new URLSearchParams(),
            "settings/presets",
        );
    }

    public async getTenantPresets(): Promise<Record<string, KboSettings>> {
        return this.get<Record<string, KboSettings>>(
            new URLSearchParams(),
            "settings/presets/tenant",
        );
    }

    public async saveTenantPreset(
        name: string,
        settings: KboSettings,
    ): Promise<void> {
        await this.put(
            new URLSearchParams(),
            "settings/presets/tenant",
            JSON.stringify({ name, settings, createOnly: false }),
        );
    }

    public async createTenantPreset(
        name: string,
        settings: KboSettings,
    ): Promise<void> {
        await this.put(
            new URLSearchParams(),
            "settings/presets/tenant",
            JSON.stringify({ name, settings, createOnly: true }),
        );
    }

    public async deleteTenantPreset(name: string): Promise<void> {
        await this.delete(
            new URLSearchParams(),
            "settings/presets/tenant",
            JSON.stringify({ name }),
        );
    }

    public async savePreset(
        name: string,
        settings: KboSettings,
    ): Promise<void> {
        await this.put(
            new URLSearchParams(),
            "settings/presets",
            JSON.stringify({ name, settings, createOnly: false }),
        );
    }

    public async createPreset(
        name: string,
        settings: KboSettings,
    ): Promise<void> {
        await this.put(
            new URLSearchParams(),
            "settings/presets",
            JSON.stringify({ name, settings, createOnly: true }),
        );
    }

    public async deletePreset(name: string): Promise<void> {
        await this.delete(
            new URLSearchParams(),
            "settings/presets",
            JSON.stringify({ name }),
        );
    }
}

export type AgentChatType = "Standard" | "KboFlowResearch";

export interface KboFlowResearchPreview {
    question?: string;
    conversationsAnalyzed?: number;
    totalConversationsInWindow?: number;
    window?: string;
    findingCount?: number;
}

/** A KBo Flow report finding, offered as a prompt above a research chat's input. */
export interface KboFlowFinding {
    findingId: string;
    title: string;
    suggestedPrompt: string;
    conversationIds: string[];
    quotes: { conversationId: string; quoteText: string }[];
}

export interface ChatSession {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    createdOn: string;
    name?: string;
    type?: AgentChatType;
    kboFlowPreview?: KboFlowResearchPreview;
    kboFlowFindings?: KboFlowFinding[];
    messages: ChatMessage[];
    /**
     * Present if this chat was generated from an async kbo analysis.
     * This can occur when a user subscribes to a search with a kbo analysis.
     */
    analysisMetadata?: AnalysisMetadata;
    /** Session-level KBo settings persisted on the system message. */
    chatSessionSettings?: KboSettings | null;
}

export interface ChatMessage {
    id: string;
    messageType: "User" | "Assistant" | "System" | "Summary";
    message: string;
    createdOn: string;
    additionalInformation?: string;
    userFeedback?: string;
    userFeedbackType?: string;
    suggestions?: ChatServerSuggestion[];
}

export interface AnalysisMetadata {
    goal: string;
    /**
     * Subscription used for this analysis. Can be undefined if the user unsubscribed
     * after a kbo analysis was already done for the filters.
     */
    subscription?: ApplicationFilterSubscription;
    /**
     * The saved filter used for the analysis. This is not a snapshot of the filter at analysis time
     * but rather the current db state.
     */
    filter?: SavedFilter;
    conversationIds?: string[];
}
